import { useState, createContext, useContext } from "react";
import { Globe, CalendarCheck, Mail, Gift, Bell, Newspaper, AlertCircle, Star, Clock, MapPin, ChevronRight } from "lucide-react";

const connections = [
  { icon: CalendarCheck, label: 'Booking widget', color: 'text-blue-500', bg: 'bg-blue-500/15', border: 'border-blue-500/25', activeBorder: 'border-blue-500/50' },
  { icon: Mail, label: 'Email collection', color: 'text-violet-500', bg: 'bg-violet-500/15', border: 'border-violet-500/25', activeBorder: 'border-violet-500/50' },
  { icon: Gift, label: 'Loyalty rewards', color: 'text-amber-500', bg: 'bg-amber-500/15', border: 'border-amber-500/25', activeBorder: 'border-amber-500/50' },
  { icon: Bell, label: 'Push notifications', color: 'text-green-500', bg: 'bg-green-500/15', border: 'border-green-500/25', activeBorder: 'border-green-500/50' },
  { icon: Newspaper, label: 'Newsletter → Blog', color: 'text-rose-500', bg: 'bg-rose-500/15', border: 'border-rose-500/25', activeBorder: 'border-rose-500/50' },
  { icon: AlertCircle, label: 'Closure alerts', color: 'text-orange-500', bg: 'bg-orange-500/15', border: 'border-orange-500/25', activeBorder: 'border-orange-500/50' },
];

const HoveredIndexContext = createContext<{ hoveredIndex: number | null; setHoveredIndex: (i: number | null) => void; cellHovered: boolean; setCellHovered: (v: boolean) => void }>({ hoveredIndex: null, setHoveredIndex: () => {}, cellHovered: false, setCellHovered: () => {} });

export const WebsiteConnectionProvider = ({ children }: { children: React.ReactNode }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [cellHovered, setCellHovered] = useState(false);
  return <HoveredIndexContext.Provider value={{ hoveredIndex, setHoveredIndex, cellHovered, setCellHovered }}>{children}</HoveredIndexContext.Provider>;
};

// Wrapper that tracks hover on the entire bento cell
export const WebsiteConnectionCell = ({ className, children }: { className?: string; children: React.ReactNode }) => {
  const { setCellHovered } = useContext(HoveredIndexContext);
  return (
    <div
      className={className}
      onMouseEnter={() => setCellHovered(true)}
      onMouseLeave={() => setCellHovered(false)}
    >
      {children}
    </div>
  );
};

// Integration cards grid — lives in BentoGrid cell, outside the visual overlay
export const WebsiteConnectionIntegrations = () => {
  const { hoveredIndex, setHoveredIndex, cellHovered } = useContext(HoveredIndexContext);
  return (
    <div className="grid grid-cols-3 gap-1.5 px-3 pb-3">
      {connections.map(({ icon: Icon, label, color, bg, border, activeBorder }, i) => {
        const isActive = i === hoveredIndex;
        return (
          <div
            key={i}
            onMouseEnter={() => setHoveredIndex(i)}
            onMouseLeave={() => setHoveredIndex(null)}
            className={`flex items-center gap-1 rounded-md border px-1.5 py-1.5 transition-all duration-300 cursor-pointer ${
              isActive
                ? `${bg} ${activeBorder} scale-[1.03] shadow-sm`
                : cellHovered
                  ? `${bg} ${border} hover:opacity-90`
                  : 'bg-muted/5 border-border/20 opacity-60 hover:opacity-80'
            }`}
          >
            <Icon className={`w-2.5 h-2.5 shrink-0 transition-colors duration-300 ${
              isActive ? color : cellHovered ? `${color} opacity-50` : 'text-muted-foreground/40'
            }`} />
            <span className={`text-3xs font-medium leading-tight transition-colors duration-300 ${
              isActive ? 'text-muted-foreground' : cellHovered ? 'text-muted-foreground/60' : 'text-muted-foreground/40'
            }`}>{label}</span>
          </div>
        );
      })}
    </div>
  );
};

// Website Connection - Browser mock only (fills available space)
export const WebsiteConnectionVisual = () => {
  const { hoveredIndex } = useContext(HoveredIndexContext);

  const hi = (idx: number, activeColor: string, inactiveColor: string) =>
    hoveredIndex === idx ? activeColor : inactiveColor;

  return (
    <div className="w-full h-full px-3 pt-3">
      <div className="bg-card/60 rounded-lg border border-border/50 overflow-hidden h-full flex flex-col">
        {/* Browser chrome */}
        <div className="flex items-center gap-1 px-2 py-1 bg-muted/30 border-b border-border/30 shrink-0">
          <div className="flex gap-0.5">
            <div className="w-1.5 h-1.5 rounded-full bg-red-400/60" />
            <div className="w-1.5 h-1.5 rounded-full bg-yellow-400/60" />
            <div className="w-1.5 h-1.5 rounded-full bg-green-400/60" />
          </div>
          <div className="flex-1 flex items-center gap-0.5 bg-background/50 rounded px-1.5 py-0.5 mx-1">
            <Globe className="w-1.5 h-1.5 text-muted-foreground/50" />
            <span className="text-3xs text-muted-foreground/60 truncate">cafemilano.cz</span>
          </div>
        </div>

        {/* Website content */}
        <div className="flex-1 min-h-0 overflow-hidden relative bg-[#1a1a2e]">
          <div className="p-2 space-y-1.5 h-full flex flex-col">
            {/* Nav */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-0.5">
                <span className="text-3xs">☕</span>
                <span className="text-3xs font-semibold text-amber-300">Café Milano</span>
              </div>
              <div className="flex gap-1.5">
                <span className="text-3xs text-amber-200/60 font-medium">Menu</span>
                <span className="text-3xs text-amber-200/60 font-medium">About</span>
                <span className="text-3xs text-amber-200/60 font-medium">Contact</span>
                <span className="text-3xs text-amber-200/60 font-medium">Book</span>
              </div>
            </div>

            {/* Hero banner */}
            <div className="rounded overflow-hidden relative h-10">
              <img
                src="https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=400&h=200&fit=crop"
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-amber-950/70 to-amber-900/30 flex flex-col justify-center px-2">
                <span className="text-3xs font-bold text-white leading-tight">Handcrafted Coffee</span>
                <span className="text-3xs text-white/70 leading-tight">Since 2018, Prague</span>
                <div className="w-12 h-2.5 rounded bg-amber-600 mt-0.5 flex items-center justify-center">
                  <span className="text-3xs text-white font-medium">Reserve a Table</span>
                </div>
              </div>
            </div>

            {/* Info bar */}
            <div className="flex items-center gap-1.5 px-0.5">
              <div className="flex items-center gap-px">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-1.5 h-1.5 text-amber-400 fill-amber-400" />
                ))}
                <span className="text-3xs text-amber-200/60 ml-0.5">4.8</span>
              </div>
              <div className="w-px h-1.5 bg-white/10" />
              <div className="flex items-center gap-px">
                <Clock className="w-1.5 h-1.5 text-amber-300/50" />
                <span className="text-3xs text-green-400">Open</span>
              </div>
              <div className="w-px h-1.5 bg-white/10" />
              <div className="flex items-center gap-px">
                <MapPin className="w-1.5 h-1.5 text-amber-300/50" />
                <span className="text-3xs text-amber-200/60">Prague</span>
              </div>
            </div>

            {/* 0: Booking widget */}
            <div className={`rounded border p-1.5 transition-all duration-300 ${hi(0, 'border-blue-400/50 bg-blue-500/15 shadow-sm shadow-blue-500/10', 'border-white/10 bg-white/5')}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-0.5">
                  <CalendarCheck className="w-2 h-2 text-blue-400/80" />
                  <span className="text-3xs text-blue-400/90 font-medium">Book a table</span>
                </div>
                <span className="text-3xs text-white/25">cafemilano.cz</span>
              </div>
              <div className="flex gap-0.5">
                <div className="flex-1 h-3 rounded bg-blue-500/10 border border-blue-400/20 flex items-center justify-center">
                  <span className="text-3xs text-blue-300/70">Today</span>
                </div>
                <div className="flex-1 h-3 rounded bg-blue-500/10 border border-blue-400/20 flex items-center justify-center">
                  <span className="text-3xs text-blue-300/70">19:00</span>
                </div>
                <div className="flex-1 h-3 rounded bg-blue-500/10 border border-blue-400/20 flex items-center justify-center">
                  <span className="text-3xs text-blue-300/70">2 guests</span>
                </div>
                <div className="w-6 h-3 rounded bg-blue-500/40 flex items-center justify-center">
                  <ChevronRight className="w-1.5 h-1.5 text-blue-300" />
                </div>
              </div>
            </div>

            {/* Menu cards */}
            <div className="flex gap-1 flex-1 min-h-0">
              <div className="flex-1 rounded border border-white/10 overflow-hidden bg-white/5 flex flex-col">
                <img
                  src="https://images.unsplash.com/photo-1514432324607-a09d9b4aefda?w=200&h=150&fit=crop"
                  alt=""
                  className="w-full flex-1 min-h-0 object-cover"
                  loading="lazy"
                />
                <div className="px-0.5 py-0.5">
                  <span className="text-3xs font-semibold text-amber-200 block">Espresso</span>
                  <span className="text-3xs text-amber-300/50">€2.80</span>
                </div>
              </div>
              <div className="flex-1 rounded overflow-hidden border border-white/10 bg-white/5 flex flex-col">
                <img
                  src="https://images.unsplash.com/photo-1572442388796-11668a67e53d?w=200&h=150&fit=crop"
                  alt=""
                  className="w-full flex-1 min-h-0 object-cover"
                  loading="lazy"
                />
                <div className="px-0.5 py-0.5">
                  <span className="text-3xs font-semibold text-amber-200 block">Cappuccino</span>
                  <span className="text-3xs text-amber-300/50">€3.50</span>
                </div>
              </div>
              <div className="flex-1 rounded border border-white/10 overflow-hidden bg-white/5 flex flex-col">
                <img
                  src="https://images.unsplash.com/photo-1534778101976-62847782c213?w=200&h=150&fit=crop"
                  alt=""
                  className="w-full flex-1 min-h-0 object-cover"
                  loading="lazy"
                />
                <div className="px-0.5 py-0.5">
                  <span className="text-3xs font-semibold text-amber-200 block">Flat White</span>
                  <span className="text-3xs text-amber-300/50">€3.80</span>
                </div>
              </div>
            </div>

            {/* 4: Blog/Newsletter post */}
            <div className={`rounded border p-1 flex items-center gap-1.5 transition-all duration-300 ${hi(4, 'border-rose-500/40 bg-rose-500/15 shadow-sm shadow-rose-500/10', 'border-white/10 bg-white/5')}`}>
              <Newspaper className={`w-2 h-2 shrink-0 transition-colors duration-300 ${hi(4, 'text-rose-400/80', 'text-amber-300/40')}`} />
              <div className="flex-1 min-w-0">
                <span className="text-3xs font-semibold text-amber-200 block leading-tight">Our Story: From Milan to Prague</span>
                <span className="text-3xs text-amber-300/40">3 min read</span>
              </div>
              <ChevronRight className={`w-1.5 h-1.5 shrink-0 transition-colors duration-300 ${hi(4, 'text-rose-400/70', 'text-white/20')}`} />
            </div>

            {/* 1: Email capture */}
            <div className={`rounded border p-1 flex items-center gap-1 transition-all duration-300 ${hi(1, 'border-violet-400/50 bg-violet-500/15 shadow-sm shadow-violet-500/10', 'border-white/10 bg-white/5')}`}>
              <Mail className="w-2 h-2 text-violet-400/80 shrink-0" />
              <div className="flex-1 h-2.5 rounded bg-violet-500/10 border border-violet-400/20 flex items-center px-1">
                <span className="text-3xs text-violet-300/50">your@email.com</span>
              </div>
              <div className="w-8 h-2.5 rounded bg-violet-500/35 flex items-center justify-center">
                <span className="text-3xs text-violet-300/90 font-medium">Subscribe</span>
              </div>
            </div>
          </div>

          {/* 3: Push notification toast overlay */}
          <div className={`absolute top-2 right-2 rounded-md border border-green-500/30 bg-card/95 backdrop-blur-sm p-1.5 flex items-center gap-1.5 shadow-lg transition-all duration-300 max-w-[65%] ${
            hoveredIndex === 3 ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
          }`}>
            <div className="w-4 h-4 rounded-full bg-green-500/15 border border-green-500/25 flex items-center justify-center shrink-0">
              <Bell className="w-2 h-2 text-green-500" />
            </div>
            <div className="space-y-0.5">
              <span className="text-3xs text-foreground/80 font-medium block">New: Happy Hour 🍻</span>
              <span className="text-3xs text-muted-foreground/50 block">Today 16:00–18:00</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
